import React, { useEffect, useState } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { Box, Button, Typography } from "@mui/material";
import { postLoginPath } from "../../utils/postLoginPath";

const cssVars = {
  "--bg-deep": "#071a1f",
  "--bg-mid": "#0d2e36",
  "--ink": "#f4f7f6",
  "--muted": "rgba(244,247,246,0.72)",
  "--accent": "#e8a54b",
  "--accent-ink": "#1a1208",
  "--glow": "rgba(56, 178, 172, 0.35)",
};

function Landing() {
  const navigate = useNavigate();
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem("adminToken");
    const user = JSON.parse(localStorage.getItem("adminUser") || "null");
    if (token && user) {
      navigate(postLoginPath(user.role), { replace: true });
      return;
    }
    const t = requestAnimationFrame(() => setReady(true));
    return () => cancelAnimationFrame(t);
  }, [navigate]);

  return (
    <Box
      component="main"
      sx={{
        ...cssVars,
        minHeight: "100vh",
        color: "var(--ink)",
        fontFamily: '"Manrope", system-ui, sans-serif',
        background: `
          radial-gradient(ellipse 90% 70% at 78% 18%, var(--glow) 0%, transparent 55%),
          radial-gradient(ellipse 60% 50% at 12% 88%, rgba(232,165,75,0.14) 0%, transparent 50%),
          linear-gradient(155deg, var(--bg-deep) 0%, var(--bg-mid) 48%, #06141a 100%)
        `,
        position: "relative",
        overflow: "hidden",
        "@keyframes rise": {
          from: { opacity: 0, transform: "translateY(18px)" },
          to: { opacity: 1, transform: "translateY(0)" },
        },
        "@keyframes drift": {
          "0%, 100%": { transform: "translate3d(0,0,0) scale(1)" },
          "50%": { transform: "translate3d(-2%, 1.5%, 0) scale(1.04)" },
        },
        "@keyframes sheen": {
          "0%": { backgroundPosition: "0% 50%" },
          "100%": { backgroundPosition: "100% 50%" },
        },
      }}
    >
      {/* Atmospheric plane — full-bleed visual */}
      <Box
        aria-hidden
        sx={{
          position: "absolute",
          inset: 0,
          backgroundImage: `
            linear-gradient(rgba(244,247,246,0.035) 1px, transparent 1px),
            linear-gradient(90deg, rgba(244,247,246,0.035) 1px, transparent 1px)
          `,
          backgroundSize: "64px 64px",
          maskImage:
            "radial-gradient(ellipse 75% 65% at 70% 40%, #000 20%, transparent 75%)",
          animation: "drift 22s ease-in-out infinite",
          pointerEvents: "none",
        }}
      />
      <Box
        aria-hidden
        sx={{
          position: "absolute",
          width: "55vmax",
          height: "55vmax",
          right: "-12%",
          top: "-18%",
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(56,178,172,0.22) 0%, transparent 68%)",
          animation: "drift 18s ease-in-out infinite reverse",
          pointerEvents: "none",
        }}
      />

      <Box
        sx={{
          position: "relative",
          zIndex: 1,
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          px: { xs: 3, md: 8 },
          py: { xs: 3, md: 4 },
        }}
      >
        <Box
          component="header"
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            opacity: ready ? 1 : 0,
            animation: ready ? "rise 0.7s ease 0.05s both" : "none",
          }}
        >
          <Button
            component={RouterLink}
            to="/login"
            variant="text"
            sx={{
              color: "var(--muted)",
              textTransform: "none",
              fontWeight: 600,
              fontFamily: "inherit",
              "&:hover": { color: "var(--ink)", background: "transparent" },
            }}
          >
            Sign in
          </Button>
        </Box>

        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            maxWidth: 760,
            py: { xs: 6, md: 8 },
          }}
        >
          <Typography
            component="p"
            sx={{
              fontFamily: '"Syne", sans-serif',
              fontWeight: 700,
              fontSize: { xs: "clamp(2.4rem, 8vw, 4.6rem)", md: "4.75rem" },
              lineHeight: 0.95,
              letterSpacing: "-0.03em",
              mb: 3,
              opacity: ready ? 1 : 0,
              animation: ready ? "rise 0.85s ease 0.12s both" : "none",
            }}
          >
            Asset Undertaking System
          </Typography>

          <Typography
            component="h1"
            sx={{
              fontFamily: '"Syne", sans-serif',
              fontWeight: 600,
              fontSize: { xs: "1.35rem", md: "1.65rem" },
              letterSpacing: "-0.02em",
              mb: 1.5,
              maxWidth: 520,
              opacity: ready ? 1 : 0,
              animation: ready ? "rise 0.85s ease 0.28s both" : "none",
            }}
          >
            Know who holds every device.
          </Typography>

          <Typography
            sx={{
              color: "var(--muted)",
              fontSize: { xs: "1rem", md: "1.1rem" },
              lineHeight: 1.55,
              maxWidth: 440,
              mb: 4,
              opacity: ready ? 1 : 0,
              animation: ready ? "rise 0.85s ease 0.4s both" : "none",
            }}
          >
            Assign, acknowledge, and verify company assets with a clear audit trail.
          </Typography>

          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 1.5,
              opacity: ready ? 1 : 0,
              animation: ready ? "rise 0.85s ease 0.52s both" : "none",
            }}
          >
            <Button
              component={RouterLink}
              to="/login"
              variant="contained"
              size="large"
              sx={{
                px: 3.5,
                py: 1.35,
                borderRadius: 1.5,
                textTransform: "none",
                fontWeight: 700,
                fontSize: "1rem",
                fontFamily: "inherit",
                color: "var(--accent-ink)",
                background: "var(--accent)",
                boxShadow: "0 12px 32px rgba(232,165,75,0.28)",
                transition: "transform 0.2s ease, box-shadow 0.2s ease",
                "&:hover": {
                  background: "#f0b45c",
                  transform: "translateY(-2px)",
                  boxShadow: "0 16px 36px rgba(232,165,75,0.36)",
                },
              }}
            >
              Sign in to continue
            </Button>
          </Box>
        </Box>

        <Typography
          component="footer"
          variant="caption"
          sx={{
            color: "rgba(244,247,246,0.45)",
            opacity: ready ? 1 : 0,
            animation: ready ? "rise 0.7s ease 0.65s both" : "none",
          }}
        >
          Secure access for authorized users only
        </Typography>
      </Box>
    </Box>
  );
}

export default Landing;
